import { FC, useState } from 'react'
import { ExperiencesTabProps } from '../types/tabTypes'
import { BookingContent } from '../../../../../Types/HotelBookingTypes'
import { i18n } from '../../../../translate/i18n'

const ExperiencesTab: FC<ExperiencesTabProps> = ({ experiencias }) => {
  const [expandedExperiences, setExpandedExperiences] = useState<Set<number>>(new Set())

  const toggleExperience = (experienceId: number) => {
    setExpandedExperiences(prev => {
      const newSet = new Set(prev)
      if (newSet.has(experienceId)) {
        newSet.delete(experienceId)
      } else {
        newSet.add(experienceId)
      }
      return newSet
    })
  }
  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return i18n.t('hospitality_room_reservation_tabs.services.status_confirmed')
      case 'pending':
        return i18n.t('hospitality_room_reservation_tabs.services.status_pending')
      case 'cancelled':
        return i18n.t('hospitality_room_reservation_tabs.services.status_cancelled')
      case 'completed':
        return i18n.t('hospitality_room_reservation_tabs.services.status_completed')
      default:
        return status
    }
  }

  if (experiencias.length === 0) {
    return (
      <div className='text-center py-10'>
        <i className='fas fa-star text-muted fs-3x mb-4'></i>
        <h3 className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.experiences.label_no_data')}</h3>
        <p className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.experiences.label_pending')}</p>
      </div>
    )
  }

  return (
    <div className='card card-xxl-stretch'>
      <div className='card-header py-5'>
        <h3 className='card-title'>
          <i className='fas fa-star me-2'></i>
          {i18n.t('hospitality_room_reservation_tabs.experiences.tab_title')}
        </h3>
        <div className='card-toolbar'>
          <span className='badge badge-light-warning'>
            {experiencias.length} {experiencias.length === 1 ? i18n.t('hospitality_room_reservation_tabs.experiences.label_experience') : i18n.t('hospitality_room_reservation_tabs.experiences.label_experiences')}
          </span>
        </div>
      </div>
      <div className='card-body'>
        <div className='row'>
          {experiencias.map((experiencia) => {
            const content: BookingContent = JSON.parse(experiencia.content || '{}')
            const isExpanded = expandedExperiences.has(experiencia.id)
            return (
              <div key={experiencia.id} className='col-12 mb-4'>
                <div className='card card-flush border border-gray-300'>
                  <div 
                    className='card-header bg-light-warning' 
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleExperience(experiencia.id)}
                  >
                    <div className='d-flex justify-content-between align-items-center w-100'>
                      <div className='d-flex align-items-center'>
                        <i className='fas fa-star text-warning fs-2x me-3'></i>
                        <div>
                          <h4 className='card-title text-warning mb-1'>{experiencia.hotelExperience.title}</h4>
                          <div className='d-flex align-items-center flex-wrap gap-3'>
                            <span className='text-muted'>
                              <i className='fas fa-calendar me-1'></i>
                              {new Date(experiencia.reservationDate).toLocaleDateString('pt-BR')}
                            </span>
                            <span className='text-muted'>
                              <i className='fas fa-users me-1'></i>
                              {experiencia.participants} {i18n.t('hospitality_room_reservation_tabs.experiences.label_people')}
                            </span>
                            <span className='text-muted'>
                              <i className='fas fa-clock me-1'></i>
                              {experiencia.hotelExperience.duration}
                            </span>
                            <span className='text-muted'>
                              <i className='fas fa-star me-1'></i>
                              {experiencia.hotelExperience.rating} ⭐
                            </span>
                            <span className='text-muted text-capitalize'>
                              <i className='fas fa-tag me-1'></i>
                              {experiencia.hotelExperience.type}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className='d-flex align-items-center'>
                        <div className='text-end me-3'>
                          <h4 className='text-warning mb-0'>{experiencia.currency} {parseFloat(experiencia.totalAmount).toFixed(2)}</h4>
                        </div>
                        <span className={`badge badge-${experiencia.status === 'confirmed' ? 'success' : experiencia.status === 'cancelled' ? 'danger' : 'warning'} me-3`}>
                          {getStatusLabel(experiencia.status)}
                        </span>
                        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-muted`}></i>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                  <div className='card-body'>
                    <div className='row mb-4'>
                      <div className='col-12'>
                        <h5 className='text-gray-700 fw-bold mb-3'>
                          <i className='fas fa-info-circle text-primary me-2'></i>
                        </h5>
                        <div 
                          className='text-gray-600'
                          dangerouslySetInnerHTML={{ __html: experiencia.hotelExperience.description }}
                        />
                      </div>
                    </div>

                    <div className='row mb-4'>
                      {content.timeSlot && (
                        <div className='col-md-3 mb-3'>
                          <small className='text-muted d-block'>{i18n.t('hospitality_room_reservation_tabs.experiences.label_time')}</small>
                          <div className='fw-bold text-capitalize'>{content.timeSlot}</div>
                        </div>
                      )}
                      {content.specialOccasion && (
                        <div className='col-md-3 mb-3'>
                          <small className='text-muted d-block'>{i18n.t('hospitality_room_reservation_tabs.experiences.label_special_occasion')}</small>
                          <div className='fw-bold text-capitalize'>{content.specialOccasion}</div>
                        </div>
                      )}
                    </div>

                    <div className='mb-4 p-3 bg-light rounded'>
                      <small className='text-muted d-block'>{i18n.t('hospitality_room_reservation_tabs.experiences.label_includes')}</small>
                      <ul className='mb-0 mt-2'>
                        {experiencia.hotelExperience.includes.map((item, index) => (
                          <li key={index} className='fw-bold'>{item}</li>
                        ))}
                      </ul>
                    </div>

                    {experiencia.specialRequests && (
                      <div className='p-3 bg-light-warning rounded'>
                        <small className='text-muted d-block'>{i18n.t('hospitality_room_reservation_tabs.experiences.label_special_requests')}</small>
                        <div className='fw-bold text-warning'>{experiencia.specialRequests}</div>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ExperiencesTab